"use client"

import { useState, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDownload, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons"
import * as XLSX from "xlsx"
import ExcelJS from "exceljs"
import SortDescendingIcon from "mdi-react/SortDescendingIcon"
import SortAscendingIcon from "mdi-react/SortAscendingIcon"
import SortIcon from "mdi-react/SortIcon"

const UnicornTable = ({ loading, tableData: initialTableData }) => {
  const [tableData, setTableData] = useState([])
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "desc" })
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredData, setFilteredData] = useState([])

  useEffect(() => {
    handleSort()
  }, [initialTableData])

  // Filter data based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(tableData)
    } else {
      const filtered = tableData.filter((item) => item.company.toLowerCase().includes(searchTerm.toLowerCase()))
      setFilteredData(filtered)
    }
  }, [tableData, searchTerm])

  const handleSort = () => {
    const newDirection = sortConfig.key === "sno" && sortConfig.direction === "desc" ? "asc" : "desc"
    const sortedData = [...initialTableData].sort((a, b) => {
      return newDirection === "desc"
        ? initialTableData.indexOf(b) - initialTableData.indexOf(a)
        : initialTableData.indexOf(a) - initialTableData.indexOf(b)
    })
    setTableData(sortedData)
    setSortConfig({ key: "sno", direction: newDirection })
  }

  const handleSortByCompany = () => {
    const newDirection = sortConfig.key === "company" && sortConfig.direction === "desc" ? "asc" : "desc"
    const sortedData = [...initialTableData].sort((a, b) => {
      const aValue = a.company.toLowerCase()
      const bValue = b.company.toLowerCase()
      if (newDirection === "asc") {
        return aValue.localeCompare(bValue)
      } else {
        return bValue.localeCompare(aValue)
      }
    })
    setTableData(sortedData)
    setSortConfig({ key: "company", direction: newDirection })
  }

  const handleSortBySector = () => {
    const newDirection = sortConfig.key === "sector" && sortConfig.direction === "desc" ? "asc" : "desc"
    const sortedData = [...initialTableData].sort((a, b) => {
      const aValue = a.sector.toLowerCase()
      const bValue = b.sector.toLowerCase()
      if (newDirection === "asc") {
        return aValue.localeCompare(bValue)
      } else {
        return bValue.localeCompare(aValue)
      }
    })
    setTableData(sortedData)
    setSortConfig({ key: "sector", direction: newDirection })
  }

  const handleSortByEntryValuation = () => {
    const newDirection = sortConfig.key === "entryValuation" && sortConfig.direction === "desc" ? "asc" : "desc"
    const sortedData = [...initialTableData].sort((a, b) => {
      const aValue = Number.parseFloat(a.entry_valuation) || 0
      const bValue = Number.parseFloat(b.entry_valuation) || 0
      return newDirection === "asc" ? aValue - bValue : bValue - aValue
    })
    setTableData(sortedData)
    setSortConfig({ key: "entryValuation", direction: newDirection })
  }

  const handleSortByValuation = () => {
    const newDirection = sortConfig.key === "valuation" && sortConfig.direction === "desc" ? "asc" : "desc"
    const sortedData = [...initialTableData].sort((a, b) => {
      const aValue = Number.parseFloat(a.valuation) || 0
      const bValue = Number.parseFloat(b.valuation) || 0
      return newDirection === "asc" ? aValue - bValue : bValue - aValue
    })
    setTableData(sortedData)
    setSortConfig({ key: "valuation", direction: newDirection })
  }

  const handleSortByEntry = () => {
    const newDirection = sortConfig.key === "entry" && sortConfig.direction === "desc" ? "asc" : "desc"
    const sortedData = [...initialTableData].sort((a, b) => {
      const aValue = new Date(a.entry_date)
      const bValue = new Date(b.entry_date)
      return newDirection === "asc" ? aValue - bValue : bValue - aValue
    })
    setTableData(sortedData)
    setSortConfig({ key: "entry", direction: newDirection })
  }

  const handleSortByLocation = () => {
    const newDirection = sortConfig.key === "location" && sortConfig.direction === "desc" ? "asc" : "desc"
    const sortedData = [...initialTableData].sort((a, b) => {
      const aValue = a.location.toLowerCase()
      const bValue = b.location.toLowerCase()
      if (newDirection === "asc") {
        return aValue.localeCompare(bValue)
      } else {
        return bValue.localeCompare(aValue)
      }
    })
    setTableData(sortedData)
    setSortConfig({ key: "location", direction: newDirection })
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  const clearSearch = () => {
    setSearchTerm("")
  }

  const base64Code =
    "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"
  const description =
    "Venture Intelligence League Table represent a pioneering initiative dedicated to meticulously tracking transactions involving companies based in India. These tables are founded on the valuation of Private Equity (PE) and Merger and Acquisition (M&A) transactions, guided by Transaction and Legal Advisory firms throughout the calendar year. The data for these tables is derived from transactions submitted by advisory firms, meticulously filtered using Venture Intelligence definitions."

  const handleDataDownload = () => {
    try {
      const tableId = "unicornTable-to-export"
      const fileName = `unicorn-tracker.xlsx`
      const table = document.getElementById(tableId)

      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet("Sheet1")

      const originalWs = XLSX.utils.table_to_sheet(table)

      const startingRow = 4

      const imageId = workbook.addImage({
        base64: base64Code,
        extension: "png",
      })

      worksheet.addImage(imageId, {
        tl: { col: 0, row: 0 },
        br: { col: 1, row: 1 },
        editAs: "twoCell",
      })

      worksheet.getCell("A2").value = description
      worksheet.getCell("A2").comment = { text: "Description for Row 2" }
      worksheet.getCell("A2").alignment = { wrapText: true, horizontal: "right", vertical: "justify" }

      worksheet.getCell("A3").value = " "
      worksheet.getCell("A3").alignment = { wrapText: true, horizontal: "center", vertical: "middle" }

      const data = XLSX.utils.sheet_to_json(originalWs, { header: 1 })
      worksheet.addRows(data, { origin: startingRow })

      const goldFill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "f0f8ff" },
      }
      ;["A4", "B4", "C4", "D4", "E4", "F4", "G4", "H4"].forEach((cell) => {
        worksheet.getCell(cell).fill = goldFill
        worksheet.getCell(cell).font = { color: { argb: "b5844c" }, bold: true }
      })

      worksheet.getColumn("A").width = 25
      worksheet.getColumn("B").width = 25
      worksheet.getColumn("C").width = 20
      worksheet.getColumn("D").width = 20
      worksheet.getColumn("E").width = 20
      worksheet.getColumn("F").width = 23
      worksheet.getColumn("G").width = 23
      worksheet.getColumn("H").width = 30

      worksheet.getRow(1).height = 40
      worksheet.getRow(2).height = 20
      worksheet.getRow(3).height = 20
      worksheet.mergeCells("A2:N2")
      worksheet.mergeCells("A3:C3")
      ;["A", "B", "C", "D", "E", "F", "G", "H"].forEach((column) => {
        worksheet.getColumn(column).alignment = { horizontal: "center" }
      })

      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        const link = document.createElement("a")
        link.href = URL.createObjectURL(blob)
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
    } catch (error) {
      console.error("Error exporting table to Excel:", error)
    }
  }

  const getSortIcon = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === "asc" ? (
        <SortAscendingIcon className="pl-2 block h-[15px]" />
      ) : (
        <SortDescendingIcon className="pl-2 block h-[15px]" />
      )
    }
    return <SortIcon className="pl-2 block h-[15px]" />
  }

  return (
    <div className="w-full">
      <>
        {/* Top Controls Bar */}
        <div className="flex justify-between items-center mb-4 px-6">
          {/* Search Filter on the Right */}
          <div className="flex-1"></div>
          <div className="flex items-center justify-between gap-4 w-full">
            {/* Company Search Filter */}
            <div className="relative">
              <div className="flex items-center bg-white border border-gray-300 rounded-lg px-3 py-2 shadow-sm hover:shadow-md transition-shadow duration-200 min-w-[300px]">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400 mr-2" size="sm" />
                <input
                  type="text"
                  placeholder="Search by company name..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="flex-1 outline-none text-sm text-gray-700 placeholder-gray-400"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="ml-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    title="Clear search"
                  >
                    <FontAwesomeIcon icon={faTimes} size="sm" />
                  </button>
                )}
              </div>
              {/* {searchTerm && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-2">
                  <div className="text-xs text-gray-600">
                    {filteredData.length} result{filteredData.length !== 1 ? "s" : ""} found
                  </div>
                </div>
              )} */}
            </div>

            {/* Download Button */}
            <button
              onClick={handleDataDownload}
              className="flex items-center p-2 hover:bg-[#A26F35]/10 rounded-full transition-colors duration-200 text-[#A26F35] hover:text-[#8B5A2B]"
              title="Download Excel"
            >
              <FontAwesomeIcon icon={faDownload} size="sm" className="mr-2" />
              <span className="text-sm font-medium">Download as Excel</span>
            </button>
          </div>
        </div>

        {/* Hidden table for Excel export */}
        <table id="unicornTable-to-export" className="hidden">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Company</th>
              <th>Sector</th>
              <th>Entry Valuation($B)</th>
              <th>Valuation ($B)</th>
              <th>Entry</th>
              <th>Location</th>
              <th>Select Investors</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((data, index) => (
              <tr key={index}>
                <td>{data.id}</td>
                <td>{data.company}</td>
                <td>{data.sector}</td>
                <td>{data.entry_valuation}</td>
                <td>{data.valuation}</td>
                <td>{data.entry_date.replace("/", " ")}</td>
                <td>{data.location}</td>
                <td>{data.select_investors}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Modern Table Design */}
        <div className="overflow-x-auto">
          <div className="min-w-full">
            {/* Header */}
            <div className="grid grid-cols-[80px_2fr_2fr_1fr_1fr_1fr_1fr_2.5fr] max-h-[88px] items-center mx-6 py-3 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 tracking-wider">
              <div className="pl-3">
                <button
                  onClick={handleSort}
                  className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                  disabled={loading}
                >
                  S.No
                  {getSortIcon("sno")}
                </button>
              </div>
              <div className="px-3">
                <button
                  onClick={handleSortByCompany}
                  className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                  disabled={loading}
                >
                  Company
                  {getSortIcon("company")}
                </button>
              </div>
              <div className="px-3">
                <button
                  onClick={handleSortBySector}
                  className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                  disabled={loading}
                >
                  Sector
                  {getSortIcon("sector")}
                </button>
              </div>
              <div className="px-3">
                <button
                  onClick={handleSortByEntryValuation}
                  className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                  disabled={loading}
                >
                  Entry Valuation($B)
                  {getSortIcon("entryValuation")}
                </button>
              </div>
              <div className="pr-3">
                <button
                  onClick={handleSortByValuation}
                  className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                  disabled={loading}
                >
                  Valuation($B)
                  {getSortIcon("valuation")}
                </button>
              </div>
              <div className="px-3">
                <button
                  onClick={handleSortByEntry}
                  className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                  disabled={loading}
                >
                  Entry
                  {getSortIcon("entry")}
                </button>
              </div>
              <div className="px-3">
                <button
                  onClick={handleSortByLocation}
                  className="flex items-center hover:text-[#bc8b20] cursor-pointer text-sm"
                  disabled={loading}
                >
                  Location
                  {getSortIcon("location")}
                </button>
              </div>
              <div className="px-3 text-sm">Select Investors</div>
            </div>

            {/* Table Body */}
            <div className="space-y-4 mt-4 px-6 pb-6">
              {loading
                ? // Skeleton rows
                  Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={`skeleton-${index}`}
                      className="grid grid-cols-[80px_2fr_2fr_1fr_1fr_1fr_1fr_2.5fr] py-4 min-h-[92px] bg-white border border-gray-200 shadow-lg animate-pulse"
                      style={{ borderRight: `4px solid #A26F35` }}
                    >
                      {/* Skeleton content */}
                    </div>
                  ))
                : filteredData.map((data, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[70px_2fr_2fr_1fr_1fr_1fr_1fr_2.5fr] rounded-xl py-4 min-h-[92px] bg-white shadow-lg hover:bg-[#FDF7E9] transition-all duration-200 hover:-translate-y-1"
                      style={{ borderRight: `3px solid #A26F35` }}
                    >
                      {/* S.No */}
                      <div className="flex items-center justify-center px-3 text-sm text-gray-900 font-medium relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                        {data.id}
                      </div>

                      {/* Company with logo */}
                      <div className="flex items-center px-3 relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                        <img
                          src={data.logo_link || "/placeholder.svg"}
                          alt={`${data.company} logo`}
                          className="w-8 h-8 rounded-md border border-gray-300 mr-2"
                        />
                        <span className="text-sm font-medium text-gray-900 hover:text-[#A26F35] transition-colors">
                          {data.company}
                        </span>
                      </div>

                      {/* Sector */}
                      <div className="flex items-center px-3 text-sm text-gray-900 relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                        {data.sector}
                      </div>

                      {/* Entry Valuation */}
                      <div className="flex items-center justify-center px-3 text-sm text-gray-900 font-medium relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                        {data.entry_valuation}
                      </div>

                      {/* Valuation */}
                      <div className="flex items-center justify-center px-3 text-sm text-gray-900 font-medium relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                        {data.valuation}
                      </div>

                      {/* Entry Date */}
                      <div className="flex items-center px-3 text-sm text-gray-900 relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                        {data.entry_date.replace("/", " ")}
                      </div>

                      {/* Location */}
                      <div className="flex items-center px-3 text-sm text-gray-900 relative after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[3px] after:bg-gradient-to-b after:from-gray-100 after:via-gray-200 after:to-gray-100 after:rounded-full after:shadow-[1px_0_3px_rgba(0,0,0,0.1)]">
                        {(data.location || "").replace(/\/(?!\s)/g, "/ ")}
                      </div>

                      {/* Select Investors */}
                      <div className="flex items-center px-3 text-sm text-gray-900 relative">
                        {data.select_investors}
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        </div>

        {/* Results count */}
        <div className="mt-6 text-center text-gray-700 pb-6">
          {loading ? (
            <div className="h-4 w-48 bg-gray-200 rounded mx-auto animate-pulse"></div>
          ) : (
            <>
              {searchTerm ? (
                <span>
                  Showing {filteredData.length} of {tableData.length} unicorn companies
                  {searchTerm && <span className="text-[#A26F35] font-medium"> matching "{searchTerm}"</span>}
                </span>
              ) : (
                `Showing ${tableData.length} unicorn companies`
              )}
            </>
          )}
        </div>
      </>
    </div>
  )
}

export default UnicornTable
